import Modal from "../../Components/Modal";
import {useTranslation} from "react-i18next";
import FileUploadButton, {FileUploadButtonV2} from "../../Components/FileUploadButton";
import React, {useEffect, useState} from "react";
import {FileDto} from "../../Apis/Models/FileDto";
import {useAgreements} from "../../Apis/Admin/AgreementAdminApi";
import {ResourceDto} from "../../Apis/Models/ResourceDto";
import InputLabel from "../../Components/InputLabel";
import {useSaveResource, useValidateResource} from "../../Apis/Admin/ResourcesAdminApi";
import MultipleResourceForm from "./MultipleResourceForm";
import Input from "../../Components/Input";
import {useResourceTypes} from "../../Apis/Admin/ResourceTypeAdminApi";
import Space from "../../Components/Space";
import Separator from "../../Components/Separator";
import {StyledText} from "../../Components/Text";
import FlexRow from "Components/FlexRow";
import Button from "../../Components/Button";
import useToast from "Hooks/useToast";

type Props = {
    visible: boolean;
    onClose: () => void;
}
const ResourceBulkUploadModal = ({visible, onClose}: Props) => {

    const { t } = useTranslation();

    const {data: resourceTypes} = useResourceTypes();
    
    const { showSuccessToast, showErrorToast } = useToast();

    const [, setFiles] = useState<FileDto[]>([]);
    const [unmatchedFiles, setUnmatchedFiles] = useState<FileDto[]>([]);
    const [resources, setResources] = useState<ResourceDto[]>([]);
    const [selectedResourceType, setSelectedResourceType] = useState<string>();
    const [selectedLink, setSelectedLink] = useState<string>();
    const [selectedPreview, setSelectedPreview] = useState<FileDto>();
    const [hasValidated, setHasValidated] = useState<boolean>(false);
    const [validationResults, setValidationResults] = useState<{agreementId: string, canValidate: boolean}[]>([]);

    const {mutateAsync: saveResourceMutation, isLoading: isSavingResource} = useSaveResource();
    const {mutateAsync: validateResourceMutation, isLoading: isValidatingResource} = useValidateResource();
    
    const { data: agreements } = useAgreements();

    useEffect(() => {
        validateResources();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resources, selectedResourceType])
    
    const transformFiles = (uploadedFiles: FileDto[]) => {
        setFiles(uploadedFiles)
        
        const localResources = resources;

        uploadedFiles.forEach(file => {

            let fileNameSplit = file.fileName.lastIndexOf('.')
            let fileNameWithoutExtension = file.fileName.substring(0, fileNameSplit);
            let rawFileNameSplit = fileNameWithoutExtension.split('-');
            let fileCompanyName = rawFileNameSplit[0].toLowerCase().trim();
            let fileCategory = rawFileNameSplit.length > 1 ? rawFileNameSplit[1].trim() : '';
            
            let matched = false;
            
            agreements?.filter(x => x.companyName.toLowerCase().includes(fileCompanyName)).forEach(agreement => {
                let existingResourceIndex = localResources.findIndex(x => x.agreementIds.includes(agreement.id));
                
                if (existingResourceIndex > -1) {
                    let existingResource = localResources[existingResourceIndex];
                    
                    existingResource.resourceFiles.push({ id: '', name: fileCategory, file: file })

                    localResources[existingResourceIndex] = existingResource;
                    
                } else {
                    let newResource: ResourceDto = {
                        id: '',
                        resourceTypeId: '',
                        link: '',
                        preview: undefined,
                        agreementIds: [agreement.id],
                        resourceFiles: [{ id: '', name: fileCategory, file: file }]
                    }
                    
                    localResources.push(newResource)
                }
                
                matched  = true;
            })
            
            if (!matched) {
                setUnmatchedFiles(unmatchedFiles => [...unmatchedFiles, file])
            }
            
            setResources(localResources)
        })

        setTimeout(() => {
            validateResources();
        }, 200)
    }
    
    const updateResource = async (index: number,  resource: ResourceDto) => {
        setResources(current => {
            let clone = [...current]
            
            clone[index] = resource
            
            return clone
        })
    }

    const validateResources = async () => {
        if (isValidatingResource) {
            return;
        }

        if (!selectedResourceType) {
            return;
        }

        setValidationResults([])

        setHasValidated(false)

        const validations = resources.map(async (resource) => {
            try {
                await validateResourceMutation({...resource, resourceTypeId: selectedResourceType!});

                setValidationResults(c => [...c, { agreementId: resource.agreementIds[0], canValidate: true}])
            }
            catch {
                setValidationResults(c => [...c, { agreementId: resource.agreementIds[0], canValidate: false}])
            }
        })

        await Promise.all(validations);

        setHasValidated(true)
    }
    
    const save = async () => {
        if (!selectedResourceType) {
            showErrorToast(t('noResourceTypeSelected'))
            return;
        }

        if (resources.length === 0) {
            showErrorToast(t('noResourcesCreated'));
            return
        }

        if (validationResults.filter(x => !x.canValidate).length > 0) {
            showErrorToast(t('resourceValidationErrorFound'));
            return
        }

        if (isValidatingResource) {
            return;
        }

        if (!hasValidated) {
            await validateResources();

            return;
        }
        
        const transformedResources = resources.map(x => {
            return {...x, preview: selectedPreview, link: selectedLink, resourceTypeId: selectedResourceType}
        })
        
        const savePromises = transformedResources.map(x => saveResourceMutation(x));
        
        await Promise.all(savePromises);
        
        setResources([]);
        setFiles([]);
        setSelectedResourceType(undefined);
        setSelectedLink(undefined);
        setSelectedPreview(undefined);
        
        showSuccessToast('success')
        
        onClose();
    }

    const getValidationResult = (agreementId: string) => {
        const response = validationResults.find(x => x.agreementId === agreementId);

        return response?.canValidate ?? undefined;
    }
    
    return (
        <Modal visible={visible} onCancel={onClose} title={t('bulkUploadResources')}>
            <Space className={"w-100"} direction={"column"}>
                
                <FlexRow justify={"space-between"}>
                    <Space>
                        <StyledText type={"Body"} weight={"Bold"}>{t('bulkSelectFiles')}</StyledText>
                        <StyledText style={{whiteSpace: 'pre-wrap'}} tag={"div"} type={"XSmall"}>{t('bulkFileDescription')}</StyledText>
                    </Space>
    
                    <FileUploadButtonV2 multiple callback={(e) => setFiles([e])} multipleCallback={transformFiles} />
                </FlexRow>
                
                <Separator />

                <div className={"w-100"}>
                    <InputLabel>
                        {t('resourceType')}
                    </InputLabel>
                    <select className={"select"} name={"resourceType"} value={selectedResourceType} onChange={e => setSelectedResourceType(e.currentTarget.value)}>
                        <option value={undefined}>{t('select')}</option>
                        {resourceTypes?.map((resourceType) => (
                            <option key={resourceType.id}
                                    value={resourceType.id}>{resourceType.title}</option>
                        ))}
                    </select>
                </div>
    
                <div className={"w-100"}>
                    <InputLabel>
                        {t('link')}
                    </InputLabel>
                    <Input placeholder={t('link')} name={"link"} value={selectedLink} onChange={e => setSelectedLink(e.currentTarget.value)} />
                </div>

                <div className={"w-100"}>
                    <InputLabel>
                        {t('preview')}
                    </InputLabel>
                    <FlexRow justify={"space-between"}>
                        <div>
                            {!!selectedPreview && (
                                <img src={selectedPreview?.url} alt={t('preview')} width={100} height={100}/>
                            )}
                        </div>
                        <FileUploadButton multiple callback={file => setSelectedPreview(file)}/>
                    </FlexRow>
                </div>
                
                <Separator />

                {unmatchedFiles.length > 0 && (
                    <>
                        <StyledText type={"Body"} weight={"Bold"}>{t('unmatchedFiles')}</StyledText>
                        {unmatchedFiles.map((file) => (
                            <div className={"w-100"}>
                                {file.fileName}
                            </div>    
                        ))}
                        <Separator />
                    </>
                )}
                

                {resources.map((resource, index) => (
                    <React.Fragment key={resource.agreementIds[0]}>
                        <MultipleResourceForm validationResult={getValidationResult(resource.agreementIds[0])} agreements={agreements ?? []} index={index} resource={resource} updateResource={updateResource} />
                    </React.Fragment>
                ))}
                
                <FlexRow justify={"end"}>
                    <Button state={(isSavingResource || isValidatingResource) ? 'Loading' : 'None'} onClick={save} text={t('save')}></Button>
                </FlexRow>
            </Space>
        </Modal>
    )

}

export default ResourceBulkUploadModal